import React, { ReactNode, useContext } from "react";
import cn from "classnames";
import { List, WindowScroller, ListRowRenderer, AutoSizer } from "react-virtualized";
import ItemRow from "./ItemRow";
import { FunnelItem } from "../tools/treeFunnel";
import { Board } from "../types";
import { getItemsMap } from "../tools";
import Tooltip, { Direction } from "../common/Tooltip";
import IconInfo from "../common/IconInfo";
import AttentionBox from "../common/AttentionBox";
import { BoardState } from "../tools/events";
import Icon from "../common/Icon";
import { SettingsContext } from "../SettingsProvider";
import { formatWeight } from "../tools";

import styles from "./ItemsInNode.module.scss";

interface Props {
    items?: FunnelItem[];
    selectedLevel?: number;
    board: Board;
    boardState: BoardState;
    trackedLabels: Set<string>;
    firstLabel?: string;
    lastLabel?: string;
    weightColumnId?: string;
    onFilterItemsOut?: (items: FunnelItem[]) => void;
}

const ItemsInNode: React.FC<Props> = function ({
    items,
    selectedLevel,
    board,
    boardState,
    trackedLabels,
    firstLabel,
    lastLabel,
    weightColumnId,
    onFilterItemsOut
}: Props) {
    const {
        context,
        settings: { unitTag }
    } = useContext(SettingsContext);
    const dark = context.theme === "dark";

    const itemsMap = getItemsMap(board);

    const showWeight = weightColumnId !== undefined;
    const weightColumnTitle =
        weightColumnId && board.columnsById[weightColumnId] ? board.columnsById[weightColumnId].title : undefined;

    const rowRenderer: ListRowRenderer = ({ index, key, style }) => {
        const item = itemsMap[items![index].id];
        let weight = undefined;
        if (showWeight) {
            const v: number = boardState.items[item.id][weightColumnId!];
            weight = v !== undefined && (v as unknown) !== "" && !isNaN(v) ? formatWeight(v, unitTag) : "(1)";
        }
        return (
            <ItemRow
                key={key}
                style={style}
                index={index}
                funnelItem={items![index]}
                selectedLevel={selectedLevel}
                item={item}
                trackedLabels={trackedLabels}
                firstLabel={firstLabel}
                lastLabel={lastLabel}
                weight={weight}
            />
        );
    };

    if (items) {
        return (
            <div className={cn(styles.container, { [styles.dark]: dark })}>
                <div className={styles.actions}>
                    {onFilterItemsOut && (
                        <div className={styles.button} onClick={(): void => onFilterItemsOut(items)}>
                            <Icon iconClass="icon__closed_eye" /> Hide {items.length} item
                            {items.length > 1 ? "s" : ""} in this node
                        </div>
                    )}
                </div>
                <div className={styles.header}>
                    <div className={cn(styles.column, styles.name)} />
                    {showWeight && (
                        <div className={cn(styles.column, styles.weight, "tooltip")}>
                            <span className={"unicodeIcon"}>⚖</span>
                            &nbsp;Weight&nbsp;
                            <IconInfo />
                            <Tooltip
                                type="dark"
                                direction={Direction.TOP}
                                text={`Values from column "${weightColumnTitle}" or 1 if value missing`}
                            />
                        </div>
                    )}
                    <div className={cn(styles.column, styles.graph, "tooltip")}>
                        Visited statuses&nbsp;
                        <IconInfo />
                        <Tooltip
                            type="dark"
                            direction={Direction.TOP}
                            text="Statuses in order they were set for each item"
                        />
                    </div>
                </div>
                <AutoSizer disableHeight>
                    {({ width }): ReactNode => (
                        <WindowScroller>
                            {({ height, isScrolling, onChildScroll, scrollTop }): ReactNode => (
                                <List
                                    autoHeight
                                    height={height}
                                    width={width}
                                    rowHeight={36}
                                    rowCount={items.length}
                                    rowRenderer={rowRenderer}
                                    isScrolling={isScrolling}
                                    onScroll={onChildScroll}
                                    scrollTop={scrollTop}
                                />
                            )}
                        </WindowScroller>
                    )}
                </AutoSizer>
                <div className={styles.footer} />
            </div>
        );
    } else {
        return (
            <div className={styles.noItems}>
                <AttentionBox title="Show list of items">
                    <p>Select node in the graph above to view list of items</p>
                </AttentionBox>
            </div>
        );
    }
};

export default ItemsInNode;
